import "./wdyr.ts";
import { StrictMode } from "react";
import { LicenseInfo as MuiLicenseInfo } from "@mui/x-license";
import { jam } from "@jam.dev/sdk";
import { createRoot } from "react-dom/client";
import WrappedApp from "@components/App/App";
import { getPath } from "./basepath.ts";
import { captureError, initAnalytics } from "./utils/analytics.ts";
import { refreshTokenClaimsAtom, tokenAtom } from "./utils/auth/auth-atoms.ts";
import { identifyUser, initAuthEffects } from "./utils/auth/auth-effects.ts";
import {
  CHUNK_FALLBACK_RELOAD_MARK,
  CHUNK_RELOAD_KEY,
  stripChunkRetryUrlParam,
} from "./utils/chunkReload.ts";
import { AppStore } from "./utils/jotai.ts";
import { initNetworkTracker } from "./utils/networkTracker.ts";

initNetworkTracker();
initAuthEffects();

stripChunkRetryUrlParam();
// Stale mark from a navigation that did not run our deferred listener (e.g. tab crash); must not block the next real failure.
sessionStorage.removeItem(CHUNK_FALLBACK_RELOAD_MARK);

// Vite emits this when a preloaded chunk fails to load. We do NOT auto-reload here:
// ChunkErrorFallback owns the one-shot retry + CHUNK_RELOAD_KEY. An index-level reload
// raced React 19’s error-boundary timing and could re-set the key right after the user
// cleared it on “Reload Page”, trapping them on “Update Available”.
window.addEventListener("vite:preloadError", (event: VitePreloadErrorEvent) => {
  // Defer past the current task so ChunkErrorFallback can set CHUNK_FALLBACK_RELOAD_MARK first.
  setTimeout(() => {
    if (sessionStorage.getItem(CHUNK_FALLBACK_RELOAD_MARK)) {
      sessionStorage.removeItem(CHUNK_FALLBACK_RELOAD_MARK);
      return;
    }
    // Key set without the mark ⇒ prior navigation already did the automatic retry; user sees manual UI or is stuck.
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      captureError("Chunk reload exhausted", event.payload);
    }
  }, 0);
});

if (import.meta.env.DEV && false) {
  void import("react-scan").then(({ scan }) => {
    scan({ enabled: true, log: true, trackUnnecessaryRenders: true });
  });
}

jam.metadata(() => {
  // biome-ignore lint/suspicious/noConsole: debug
  console.log("jam:", AppStore.get(tokenAtom));
  return { ...AppStore.get(tokenAtom) };
});

if (import.meta.env.PROD) {
  void fetch(getPath("/api/info"), {
    headers: { "Content-Type": "application/json" },
  })
    .then(async (res) => {
      const data = (await res.json()) as {
        report_analytics: boolean;
        posthog_config?: { api_key: string; [key: string]: unknown };
      };

      if (data.report_analytics && data.posthog_config) {
        const { api_key, ...options } = data.posthog_config;
        // All static options should be passed from the database and not hard coded.
        initAnalytics(api_key, options, () => {
          const accessClaims = AppStore.get(tokenAtom);
          const refreshClaims = AppStore.get(refreshTokenClaimsAtom);
          identifyUser(accessClaims, refreshClaims);
        });
      }
    })
    .catch((e: unknown) => {
      console.warn("Analytics init failed:", e);
    });
}

MuiLicenseInfo.setLicenseKey(
  "85d0e54ab7a55053c28dad7c6279b2f7Tz0xMjIzMTcsRT0xNzk1ODIzOTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MSxBVD1tdWx0aSxLVj0y"
);

const root = createRoot(document.getElementById("root") as HTMLDivElement);
root.render(
  <StrictMode>
    <WrappedApp />
  </StrictMode>
);
